import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Alert,
} from "antd";
import adminApi from "../../services/adminApi";
import PageHeader from "../../components/PageHeader";

const SettingsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadSettings = () => {
    setLoadError(null);
    adminApi
      .getSettings()
      .then((res) => {
        form.setFieldsValue(res.data.data);
      })
      .catch(() => setLoadError("Failed to load settings"));
  };

  useEffect(() => {
    loadSettings();
  }, [form]);

  const save = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await adminApi.updateSettings(values);
    } catch (e: any) {
      // API errors toasted by interceptor; form validation stays inline
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    try {
      await adminApi.migrateStock();
    } catch {
      // API errors toasted by interceptor
    }
  };

  return (
    <div>
      <PageHeader
        title="Pharmacy Settings"
        extra={
          <Space size={8}>
            <Button onClick={runMigration}>Migrate Legacy Stock</Button>
            <Button type="primary" loading={loading} onClick={save}>
              Save
            </Button>
          </Space>
        }
      />
      <div className="page-panel">
        {loadError && (
          <Alert
            type="error"
            showIcon
            message={loadError}
            action={
              <Button size="small" onClick={loadSettings}>
                Retry
              </Button>
            }
            style={{ marginBottom: 10 }}
          />
        )}
        <Form form={form} layout="vertical" style={{ maxWidth: 800 }}>
          <Form.Item name="pharmacyName" label="Pharmacy Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Space style={{ display: "flex" }} size={8}>
            <Form.Item name="gstin" label="GSTIN" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="drugLicenseNumber" label="Drug License No." style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="stateCode" label="State Code" style={{ width: 120 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ display: "flex" }} size={8}>
            <Form.Item name="phone" label="Phone" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name={["address", "street"]} label="Street">
            <Input />
          </Form.Item>
          <Space style={{ display: "flex" }} size={8}>
            <Form.Item name={["address", "city"]} label="City" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name={["address", "state"]} label="State" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name={["address", "zipCode"]} label="PIN" style={{ width: 120 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ display: "flex" }} size={8}>
            <Form.Item name="salePrefix" label="Sale Invoice Prefix">
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="purchasePrefix" label="Purchase Prefix">
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="defaultGstRate" label="Default GST %">
              <InputNumber min={0} max={100} />
            </Form.Item>
            <Form.Item name="taxMode" label="Default Tax Mode">
              <Select style={{ width: 160 }}>
                <Select.Option value="cgst_sgst">CGST + SGST</Select.Option>
                <Select.Option value="igst">IGST</Select.Option>
              </Select>
            </Form.Item>
          </Space>
          <Form.Item name="billFooter" label="Bill Footer">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="terms" label="Terms">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default SettingsPage;
